import type { ChangeEvent, KeyboardEventHandler } from 'react';
import { useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import StopIcon from '@mui/icons-material/Stop';
import Switch from '@mui/material/Switch';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import { InputControlsContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { useAvatarPanel } from '@trinity/chat-components/components/DigitalTwin/AvatarPanel.context';
import { InteractionModeDropdown } from '@trinity/chat-components/components/DigitalTwin/InteractionModeDropdown';
import { SoundInput } from '@trinity/chat-components/components/icons/SoundInput';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip';
import { useGroupFeatures } from '@trinity/chat-components/containers/Chat';
import {
  useHasChatVoiceInputEnabled,
  useHasChatVoiceModeEnabled,
} from '@trinity/chat-components/containers/Chat/selectors';
import { VoiceModeContext } from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context.js';
import { pulse } from '@trinity/chat-components/utils';
import { shouldForwardProp } from '@trinity/chat-components/utils/emotion';
import {
  SubmitMessageContext,
  ChatSessionInputContext,
  UploadFilesContext,
  IsPreparingUploadContext,
  ChatHasAccessContext,
} from './context';
import { useCancelQueryMutation } from './hooks/useCancelQueryMutation';
import type { MicState } from './hooks/useSpeechRecognition';
import { UploadFilesButton, uploadButtonHeight } from './Input.Upload';
import { SendIcon } from './SendIcon';

export const UnsubmittedMessage = () => {
  const ctx = useContext(InputControlsContext);
  const input = useContext(ChatSessionInputContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const voicemode = useContext(VoiceModeContext);
  const { state: submitState, onSubmit: handleSubmit } = useContext(SubmitMessageContext);
  const hasChatVoiceInputEnabled = useHasChatVoiceInputEnabled();
  const features = useGroupFeatures();
  const canUploadFiles = features.chatFileUploads;
  const { isDigitalTwin } = useAvatarPanel();

  const uploadFiles = useContext(UploadFilesContext);
  const isPreparingUpload = useContext(IsPreparingUploadContext);

  const hasAccess = useContext(ChatHasAccessContext);

  const inputDisabled = useMemo(() => {
    return submitState.status !== 'inactive';
  }, [submitState.status]);

  // The mic button is enabled when input is empty (so the user can speak to
  // populate it from scratch), but disabled when:
  // - a query or file upload is in progress, OR
  // - the user has no access (mic is useless if they can't submit anything).
  const micDisabled = useMemo(() => {
    return !hasAccess || submitState.status === 'submitting-query' || submitState.status === 'uploading-file';
  }, [hasAccess, submitState.status]);

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (e.shiftKey) {
          dispatch({
            type: 'input-changed',
            payload: {
              value: `${input}\n`,
            },
          });
        } else if (!inputDisabled) {
          handleSubmit();
        }
      }
    },
    [inputDisabled, dispatch, handleSubmit, input]
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      dispatch({
        type: 'input-changed',
        payload: { value: e.target.value },
      });
    },
    [dispatch]
  );

  const micState = ctx.speechRecognition.micState;

  const MicButtonIcon = useCallback(() => {
    if (micState === 'listening' || micState === 'waiting-for-permission') {
      return (
        <SoundInput ref={ctx.soundLevelRef} color="white" paused={micState === 'waiting-for-permission'} />
      );
    } else if (micState === 'error') {
      return <StyledMicOffIcon />;
    } else {
      return <StyledMicIcon />;
    }
  }, [ctx.soundLevelRef, micState]);

  const showVoiceFeatures = useMemo(() => {
    return ctx.speechRecognition.isSupported() && hasChatVoiceInputEnabled;
  }, [ctx.speechRecognition, hasChatVoiceInputEnabled]);

  const hasVoiceModeFeature = useHasChatVoiceModeEnabled();

  return (
    <Root>
      <Wrap>
        <TextArea
          ref={ctx.textAreaRef}
          placeholder={ctx.speechRecognition.listening ? 'Speak your question.' : 'Type your question.'}
          minRows={3}
          maxRows={15}
          value={input}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
        />

        <Buttons>
          <InlineButtons>
            {showVoiceFeatures && (
              <Tooltip title={micState === 'error' ? ctx.speechRecognition.errorMsg : null} placement="top">
                <StyledMicButton
                  disabled={micDisabled || micState === 'error'}
                  micState={micState}
                  onClick={ctx.onClickMicrophone}
                >
                  <MicButtonIcon />
                </StyledMicButton>
              </Tooltip>
            )}

            <SubmitButton />
          </InlineButtons>
          {isDigitalTwin ? (
            // Digital Twin: show a Text / Voice / Video dropdown
            <VoiceModeButton>
              <InteractionModeDropdown />
            </VoiceModeButton>
          ) : (
            hasVoiceModeFeature && (
              // Standard chat: keep the existing Voice toggle switch
              <VoiceModeButton>
                <VoiceModeInput>
                  <div>Voice</div>
                  <Switch value={voicemode.enabled} onChange={ctx.onClickVoiceMode} />
                </VoiceModeInput>
              </VoiceModeButton>
            )
          )}
        </Buttons>
        {canUploadFiles && <StyledUploadButton isLoading={isPreparingUpload} onSelect={uploadFiles} />}
      </Wrap>
    </Root>
  );
};

const SubmitButton = () => {
  const { onClickSubmit } = useContext(InputControlsContext);
  const { state, onSubmit } = useContext(SubmitMessageContext);
  const cancelQuery = useCancelQueryMutation();

  const disabled = useMemo(() => {
    return ['disabled', 'uploading-file'].includes(state.status);
  }, [state.status]);

  const handleClick = useCallback(async () => {
    if (disabled) return;

    if (state.status === 'inactive') {
      return onClickSubmit();
    } else if (state.status === 'submitting-query') {
      await cancelQuery.mutateAsync();
    }
  }, [cancelQuery, onClickSubmit, onSubmit]);

  const isLoading = state.status === 'submitting-query';

  return (
    <Button disabled={disabled} isLoading={isLoading} onClick={handleClick}>
      {isLoading ? <StyledStopIcon /> : <StyledSendIcon disabled={disabled} size={40} />}
    </Button>
  );
};

const baseTextareaPadding = 10;
const submitBtnSize = 38;
const buttonsSpacing = 10;
const buttonsCount = 2;

const Root = styled('div')({
  // width: messageLaneWidth,
  width: '100%',
  margin: '0 auto',
  position: 'relative',
  // paddingTop: '70px',
});

const Wrap = styled.div({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  marginBottom: 10,
});

const TextArea = styled(TextareaAutosize)(({ theme }) => ({
  boxSizing: 'border-box',
  backgroundColor: theme.palette.white.main,
  borderRadius: 5,
  border: `2px solid ${theme.palette.gray.light2}`,
  outline: 'none',
  resize: 'none',
  padding: baseTextareaPadding,
  paddingRight: baseTextareaPadding + submitBtnSize * buttonsCount + buttonsSpacing * (buttonsCount - 1),
  paddingBottom: baseTextareaPadding + uploadButtonHeight,
  width: '100%',

  fontFamily: theme.fonts.regular,
  fontSize: 16,
  color: theme.palette.black.light1,
  transition: 'border 0.3s',

  '&:hover': {
    border: `2px solid ${theme.palette.primary.main}`,
  },

  '&:focus-visible': {
    outline: 'none',
  },

  '&:disabled': {
    cursor: 'not-allowed',
  },
}));

const Buttons = styled.div({
  // display: 'flex',
  // gap: buttonsSpacing,
  position: 'absolute',
  right: 10,
});

const InlineButtons = styled.div({
  display: 'flex',
  gap: buttonsSpacing,
});

const Button = styled('button', { shouldForwardProp: shouldForwardProp('isLoading') })<{
  isLoading?: boolean;
}>`
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};

  width: ${submitBtnSize}px;
  height: ${submitBtnSize}px;
  border-radius: 50%;

  display: flex;
  justify-content: center;
  align-items: center;

  transition:
    background-color 0.3s,
    border 0.3s;

  background-color: ${(props) =>
    props.disabled
      ? props.theme.palette.gray.light1
      : props.isLoading
        ? '#f2f2f2'
        : props.theme.palette.sentiment.primary.main};
  border: ${(props) => (props.isLoading ? '2px solid #d4d8de' : '2px solid transparent')};

  &:hover:not([disabled]) {
    background-color: ${(props) =>
      props.isLoading ? '#e6e6e6' : props.theme.palette.sentiment.primary.dark};
  }
`;

const VoiceModeButton = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  width: 100%;
  height: 40px;
`;

const VoiceModeInput = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-family: var(--font-bold);
`;

const StyledStopIcon = styled(StopIcon)`
  color: #4c4c4c;
`;

type SendProps = {
  disabled: boolean;
};

const StyledSendIcon = styled(SendIcon, { shouldForwardProp: shouldForwardProp('disabled') })<SendProps>`
  padding-left: 4px;
  fill: ${(props) => (props.disabled ? props.theme.palette.gray.dark1 : props.theme.palette.white.main)};

  transition: fill 0.3s;
`;

const StyledMicIcon = styled(MicIcon, {})`
  color: var(--black-l);
`;

const StyledMicOffIcon = styled(MicOffIcon)`
  color: var(--black-l);
`;

type StyledMicButtonProps = {
  micState: MicState;
};

const StyledMicButton = styled('button')<StyledMicButtonProps>(({ theme, micState, disabled }) => ({
  cursor: disabled ? 'not-allowed' : 'pointer',

  width: `${submitBtnSize}px`,
  height: `${submitBtnSize}px`,
  borderRadius: '50%',

  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  transition: 'background-color 0.3s',

  boxShadow: `0 0 0 0 ${theme.palette.sentiment.primary.main}`,
  animation: micState === 'listening' ? `${pulse(theme.palette.sentiment.primary.main)} 4s infinite` : 'none',
  backgroundColor:
    micState === 'listening' || micState === 'waiting-for-permission'
      ? theme.palette.sentiment.primary.main
      : 'none',

  '&:hover:not([disabled])': {
    backgroundColor:
      micState === 'listening' || micState === 'waiting-for-permission'
        ? undefined
        : disabled
          ? theme.palette.gray.light1
          : theme.palette.sentiment.primary.light,
  },

  '&:hover:not([disabled]) svg': {
    color: theme.palette.sentiment.primary.dark,
    //backgroundColor: listening ? undefined : disabled ? theme.palette.gray.light1 : theme.palette.sentiment.primary.light,
  },
}));

const StyledUploadButton = styled(UploadFilesButton)({
  position: 'absolute',
  bottom: baseTextareaPadding,
  left: baseTextareaPadding,
});
